import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';

import API from 'api';
import { USER_ROLE } from 'api/users/users.model';
import { UTILITY_QUERY, SETTINGS_KEY } from 'api/utility/utility.model';
import Button from 'components/Buttons';
import ProtectedComponent from 'components/ProtectedComponent';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import { COMPANY } from 'models/company.model';

import * as Styled from './Menu.styles';

const routesAbsolutePaths = {
  newCertificate: '/home/new-certificate',
  certificates: '/home/certificates',
  // analytics: '/home/analytics',
  testResults: '/home/test-results',
  salesDepartments: '/home/sales-departments',
  purchasingDepartments: '/home/purchasing-departments',
  purchasing: '/home/purchasing',
  sales: '/home/sales',
  logout: '/logout',
};

const Menu = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { currentUser, handleLogout } = useAuthenticatedContext();
  const [currentKey, setCurrentKey] = useState('');

  const { data = [] } = useQuery([UTILITY_QUERY.GET_SETTINGS], () => API.utility.getSettings(), {
    useErrorBoundary: true,
  });

  const incentivesUrl =
    data.find((setting) => setting.key === SETTINGS_KEY.DOCUMENT_PREVIEW_URL)?.value || '/home';

  useEffect(() => {
    const currentKeyLocal = Object.values(routesAbsolutePaths).find((keyPath) =>
      location.pathname.startsWith(keyPath)
    );
    setCurrentKey(currentKeyLocal ?? '');
  }, [location.pathname]);

  const allRoles = Object.keys(USER_ROLE) as USER_ROLE[];

  return (
    <Styled.Menu key="menu" mode="inline" selectedKeys={[currentKey]}>
      <Styled.ItemGroup key="g1" title={t('nav.certificates')}>
        <ProtectedComponent
          role={(Object.keys(USER_ROLE) as USER_ROLE[]).filter((r) => r !== USER_ROLE.VISITOR)}
          company={(Object.values(COMPANY) as COMPANY[]).filter((c) => c !== COMPANY.AUTHORITIES)}
        >
          <Styled.ButtonWrapperItem key={routesAbsolutePaths.newCertificate}>
            <Button>
              <Styled.InsideButtonLink to={routesAbsolutePaths.newCertificate}>
                {t('nav.new')}
              </Styled.InsideButtonLink>
            </Button>
          </Styled.ButtonWrapperItem>
        </ProtectedComponent>

        <Styled.Item key={routesAbsolutePaths.certificates}>
          <Link to={routesAbsolutePaths.certificates}>{t('nav.certificates')}</Link>
        </Styled.Item>

        {/* <Styled.Item key={routesAbsolutePaths.analytics}>
          <Link to={routesAbsolutePaths.analytics}>{t('nav.analytics')}</Link>
        </Styled.Item> */}

        <Styled.Item key={incentivesUrl}>
          <a href={incentivesUrl} target="_blank" rel="noopener noreferrer">
            {t('nav.incentives')}
          </a>
        </Styled.Item>
      </Styled.ItemGroup>

      <ProtectedComponent
        role={allRoles}
        company={[
          COMPANY.CHEMISTRY,
          COMPANY.BATTERY_CELLS,
          COMPANY.BATTERY_PACKS,
          COMPANY.AUTHORITIES,
        ]}
      >
        <>
          <Styled.Separator />

          <Styled.ItemGroup key="g2" title={t('nav.documents')}>
            <Styled.Item key={routesAbsolutePaths.testResults}>
              <Link to={routesAbsolutePaths.testResults}>{t('nav.testResults')}</Link>
            </Styled.Item>

            {currentUser?.company === COMPANY.BATTERY_PACKS && (
              <Styled.Item key={routesAbsolutePaths.purchasingDepartments}>
                <Link to={routesAbsolutePaths.purchasingDepartments}>
                  {t('nav.purchasingDepartments')}
                </Link>
              </Styled.Item>
            )}

            {currentUser?.company &&
              [COMPANY.BATTERY_CELLS, COMPANY.BATTERY_PACKS].includes(currentUser.company) && (
                <Styled.Item key={routesAbsolutePaths.salesDepartments}>
                  <Link to={routesAbsolutePaths.salesDepartments}>{t('nav.salesDepartments')}</Link>
                </Styled.Item>
              )}

            {/* <Styled.Item key={routesAbsolutePaths.purchasing}>
              <Link to={routesAbsolutePaths.purchasing}>{t('nav.purchasing')}</Link>
            </Styled.Item>
            <Styled.Item key={routesAbsolutePaths.sales}>
              <Link to={routesAbsolutePaths.sales}>{t('nav.sales')}</Link>
            </Styled.Item> */}
          </Styled.ItemGroup>
        </>
      </ProtectedComponent>

      <Styled.ItemGroup key="g3" className="bottom-item">
        <Styled.Item onClick={handleLogout} key={routesAbsolutePaths.logout}>
          {t('nav.logout')}
        </Styled.Item>
      </Styled.ItemGroup>
    </Styled.Menu>
  );
};

export default Menu;
